// The help articles' markdown subset (see HELP-CONTRACT): `##` / `###` headings,
// paragraphs, `1.` steps, `-` bullets (one level), `> Note:` callouts, `**bold**`
// and `[text](/help/<audience>/<slug>)` links. Parsed to a small AST that the
// view renders as React elements — no HTML string, no dangerouslySetInnerHTML —
// and that the tests lint, so an article using anything else fails with its
// file name and line.

export type Inline =
  | { type: 'text'; text: string }
  | { type: 'bold'; children: Inline[] }
  | { type: 'link'; href: string; children: Inline[] };

export type Block =
  | { type: 'h2'; text: string; anchor: string }
  | { type: 'h3'; text: string; anchor: string }
  | { type: 'p'; children: Inline[] }
  | { type: 'ol'; items: Inline[][] }
  | { type: 'ul'; items: Inline[][] }
  | { type: 'note'; children: Inline[] };

export interface MarkdownProblem {
  /** 1-based line within the body. */
  line: number;
  message: string;
}

export interface ParsedMarkdown {
  blocks: Block[];
  problems: MarkdownProblem[];
}

/** "Invite a buyer" -> "invite-a-buyer": lowercase, runs of non-alphanumerics -> "-". */
export function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** The plain text of inline nodes (markers dropped). */
export function inlineText(nodes: Inline[]): string {
  return nodes.map((n) => (n.type === 'text' ? n.text : inlineText(n.children))).join('');
}

const INLINE_RE = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/;

/** Parse `**bold**` and `[text](href)` inside a line of text. */
export function parseInline(src: string): Inline[] {
  const out: Inline[] = [];
  let rest = src;
  while (rest.length > 0) {
    const m = INLINE_RE.exec(rest);
    if (!m) {
      out.push({ type: 'text', text: rest });
      break;
    }
    if (m.index > 0) out.push({ type: 'text', text: rest.slice(0, m.index) });
    if (m[1] !== undefined) {
      out.push({ type: 'bold', children: parseInline(m[1]) });
    } else {
      out.push({ type: 'link', href: m[3] ?? '', children: parseInline(m[2] ?? '') });
    }
    rest = rest.slice(m.index + m[0].length);
  }
  return out;
}

/** Every link href found in the blocks, in document order. */
export function linkHrefs(blocks: Block[]): string[] {
  const hrefs: string[] = [];
  const walk = (nodes: Inline[]) => {
    for (const n of nodes) {
      if (n.type === 'link') hrefs.push(n.href);
      if (n.type !== 'text') walk(n.children);
    }
  };
  for (const b of blocks) {
    if (b.type === 'p' || b.type === 'note') walk(b.children);
    else if (b.type === 'ol' || b.type === 'ul') b.items.forEach(walk);
  }
  return hrefs;
}

const OL_RE = /^\d+\.\s+(.*)$/;
const UL_RE = /^-\s+(.*)$/;

/** True for a line that starts a block of its own (ends a running paragraph). */
function startsBlock(line: string): boolean {
  return (
    /^#{1,6}\s/.test(line) ||
    OL_RE.test(line) ||
    UL_RE.test(line) ||
    line.startsWith('>') ||
    line.startsWith('```')
  );
}

/**
 * Parse an article body. Never throws: anything outside the subset is reported
 * in `problems` (with its line) and otherwise rendered as plain text, so a bad
 * article still shows while the tests flag it.
 */
export function parseMarkdown(body: string): ParsedMarkdown {
  const lines = body.replace(/\r\n?/g, '\n').split('\n');
  const blocks: Block[] = [];
  const problems: MarkdownProblem[] = [];
  const seenAnchors = new Map<string, number>();
  const problem = (line: number, message: string) => problems.push({ line, message });
  const anchorFor = (text: string): string => {
    const base = slugifyHeading(text) || 'section';
    const n = (seenAnchors.get(base) ?? 0) + 1;
    seenAnchors.set(base, n);
    return n === 1 ? base : `${base}-${n}`;
  };

  let inFence = false;
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i] ?? '';
    const n = i + 1;
    const line = raw.trimEnd();

    if (line.startsWith('```')) {
      problem(n, 'code blocks are not allowed');
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    if (line.trim() === '') continue;

    // Things outside the subset, flagged wherever they appear.
    if (/<\/?[a-zA-Z][^>]*>/.test(line)) problem(n, 'HTML is not allowed');
    if (/!\[[^\]]*\]\(/.test(line)) problem(n, 'images are not allowed');
    if (/^\s*\|.*\|\s*$/.test(line)) problem(n, 'tables are not allowed');
    if (/^\s+([-*+]|\d+\.)\s/.test(line))
      problem(n, 'nested lists are not allowed (one level only)');

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      const level = (heading[1] ?? '').length;
      const text = inlineText(parseInline((heading[2] ?? '').trim()));
      if (level === 2) blocks.push({ type: 'h2', text, anchor: anchorFor(text) });
      else if (level === 3) blocks.push({ type: 'h3', text, anchor: anchorFor(text) });
      else if (level === 1)
        problem(n, 'use "## " for headings; the title comes from the frontmatter');
      else problem(n, `"${heading[1]}" headings are not allowed; use "##" or "###"`);
      continue;
    }

    if (line.startsWith('>')) {
      const parts: string[] = [];
      let j = i;
      for (; j < lines.length; j++) {
        const l = (lines[j] ?? '').trimEnd();
        if (!l.startsWith('>')) break;
        parts.push(l.replace(/^>\s?/, ''));
      }
      blocks.push({ type: 'note', children: parseInline(parts.join(' ').trim()) });
      i = j - 1;
      continue;
    }

    const ordered = OL_RE.test(line);
    if (ordered || UL_RE.test(line)) {
      const re = ordered ? OL_RE : UL_RE;
      const items: Inline[][] = [];
      let j = i;
      for (; j < lines.length; j++) {
        const l = (lines[j] ?? '').trimEnd();
        const m = re.exec(l);
        if (m) {
          items.push(parseInline((m[1] ?? '').trim()));
        } else if (
          l.trim() !== '' &&
          /^\s{2,}\S/.test(l) &&
          items.length > 0 &&
          !startsBlock(l.trim())
        ) {
          // A wrapped continuation line belongs to the previous item.
          const prev = items.pop() ?? [];
          items.push([...prev, ...parseInline(` ${l.trim()}`)]);
        } else {
          break;
        }
      }
      blocks.push({ type: ordered ? 'ol' : 'ul', items });
      i = j - 1;
      continue;
    }

    // A paragraph: this line plus following lines until a blank or a block start.
    const parts = [line.trim()];
    let j = i + 1;
    for (; j < lines.length; j++) {
      const l = (lines[j] ?? '').trimEnd();
      if (l.trim() === '' || startsBlock(l)) break;
      parts.push(l.trim());
    }
    blocks.push({ type: 'p', children: parseInline(parts.join(' ')) });
    i = j - 1;
  }

  return { blocks, problems };
}
